import { NarrativeThread, Scene, type ThreadVariant } from '@lib/data/story';
import { useDebouncedValue } from '@lib/hooks/debounce';
import { VideoPlayerStore } from '@stores/VideoPlayer';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import css from './ThreadLanes.module.scss';

type Props = {
  /** Already sorted and `end`-repaired by `sortScenes`. */
  scenes: Scene[];
  threads: NarrativeThread[];
  colorByThreadId: Map<number, string>;
  /**
   * The secondary channel per thread, drawn here as a stripe pattern. Colour alone cannot
   * separate twenty threads in one hue family, and every thread is on screen at once as a
   * lane — see `threadVariant`.
   */
  variantByThreadId: Map<number, ThreadVariant>;
  /** Must be > 0 — every position here is a fraction of it. */
  durationSeconds: number;
  selectedThreadId: number | null;
  onSelectThread: (threadId: number | null) => void;
  store: VideoPlayerStore;
};

/**
 * Where each narrative thread runs across the media: one lane per thread, under a playhead.
 *
 * Scene boundaries are deliberately NOT drawn here. They live on the player's own scrub bar,
 * and marking them in both places put two near-identical rulers on one screen — with the trim
 * timeline that made three. What no other surface can show is this: whether a thread is spread
 * through the whole media or clumped into one stretch, and how two threads overlap.
 */
/** Variant 0 is bare — the first group of five separates on colour alone. */
const variantClass = (variant: ThreadVariant | undefined) =>
  variant === 1
    ? css.runVariant1
    : variant === 2
      ? css.runVariant2
      : variant === 3
        ? css.runVariant3
        : undefined;

export const ThreadLanes = observer(
  ({
    scenes,
    threads,
    colorByThreadId,
    variantByThreadId,
    durationSeconds,
    selectedThreadId,
    onSelectThread,
    store,
  }: Props) => {
    const currentTime = useDebouncedValue(store.currentTime, 50);

    // A run reaching past either end would stretch the lane and shift every other run in it.
    const pct = (seconds: number) =>
      `${Math.min(100, Math.max(0, (seconds / durationSeconds) * 100))}%`;

    return (
      <div className={css.lanes}>
        {threads.map((thread) => {
          const selected = thread.thread_id === selectedThreadId;
          const memberIds = new Set(thread.scenes_included ?? []);
          const title = thread.title ?? `Thread ${thread.thread_id}`;
          return (
            <button
              key={thread.thread_id}
              type="button"
              className={classNames(css.lane, {
                // Dimming the others rather than hiding them keeps the lane count — and so
                // every run's vertical position — identical whether or not one is picked.
                [css.laneDimmed]: selectedThreadId !== null && !selected,
              })}
              // Out of the tab order on purpose: the chip below does the same job with a real
              // label and a real hit target, and 4 extra stops in front of the scene list buys
              // a keyboard user nothing. Still reachable to a screen reader, still clickable.
              tabIndex={-1}
              title={title}
              aria-pressed={selected}
              aria-label={`${title} — ${memberIds.size} scenes`}
              onClick={() => onSelectThread(selected ? null : thread.thread_id)}
            >
              {scenes
                .filter((scene) => memberIds.has(scene.scene_id))
                .map((scene) => {
                  const start = scene.start ?? 0;
                  const end = scene.end ?? start;
                  return (
                    // Scenes tile the media, so consecutive members render flush against each
                    // other and read as one unbroken run without any merging pass.
                    <span
                      key={scene.scene_id}
                      className={classNames(
                        css.run,
                        variantClass(variantByThreadId.get(thread.thread_id)),
                      )}
                      style={{
                        left: pct(start),
                        width: pct(end - start),
                        background: colorByThreadId.get(thread.thread_id),
                      }}
                    />
                  );
                })}
            </button>
          );
        })}

        <span className={css.needle} style={{ left: pct(currentTime) }} />
      </div>
    );
  },
);
